import React, { useEffect, useState } from 'react';
import './App.css';
import appIcon from './assets/App_Icon_PNG.png';
import iphoneBezel from './assets/iPhone_Bezel.PNG';

const features = [
  {
    icon: 'pdf',
    title: 'Image to PDF',
    formats: 'JPEG · PNG · HEIC → PDF',
    description:
      'Combine any number of JPEG, PNG, or HEIC photos into a single PDF in seconds. Reorder pages before you export.',
    color: 'orange',
  },
  {
    icon: 'image',
    title: 'Convert Images',
    formats: 'JPEG ↔ PNG · HEIC → JPEG',
    description:
      'JPEG to PNG, PNG to JPEG, or HEIC to JPEG. Perfect for sharing photos that won’t open on other devices.',
    color: 'blue',
  },
  {
    icon: 'audio',
    title: 'Extract Audio',
    formats: 'MP4 · MOV → M4A',
    description:
      'Pull the audio track from any MP4 or MOV video and save it as an M4A. Great for voice memos, interviews, or music.',
    color: 'purple',
  },
  {
    icon: 'video',
    title: 'Convert Video',
    formats: 'MOV → MP4',
    description:
      'Convert MOV files to universally compatible MP4 with a single tap.',
    color: 'amber',
  },
];

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    tagline: '5 free conversions to get started',
    features: [
      '5 total conversions',
      'All conversion types',
      'On-device processing',
      'No account required',
    ],
    cta: 'Start free',
    highlight: false,
  },
  {
    name: 'Monthly',
    price: '$2.99',
    period: 'per month',
    tagline: 'Flexible — cancel any time',
    features: [
      'Unlimited conversions',
      'All future features',
      'Full conversion history',
      'Cancel any time',
    ],
    cta: 'Go Pro Monthly',
    highlight: false,
  },
  {
    name: 'Annual',
    price: '$9.99',
    period: 'per year',
    tagline: 'Save over 70% vs. monthly',
    features: [
      'Unlimited conversions',
      'All future features',
      'Full conversion history',
      'Best value for most users',
    ],
    cta: 'Go Pro Annual',
    highlight: true,
    badge: 'Most popular',
  },
  {
    name: 'Lifetime',
    price: '$19.99',
    period: 'one-time',
    tagline: 'Pay once, yours forever',
    features: [
      'Unlimited conversions',
      'All future features',
      'No recurring charges',
      'Lifetime updates',
    ],
    cta: 'Buy Lifetime',
    highlight: false,
  },
];

const Icon = ({ name }) => {
  const common = {
    width: 28,
    height: 28,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  switch (name) {
    case 'pdf':
      return (
        <svg {...common}>
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M14 3v5h5" />
          <path d="M9 13h6M9 17h4" />
        </svg>
      );
    case 'image':
      return (
        <svg {...common}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="9" cy="9" r="2" />
          <path d="m21 15-5-5L5 21" />
        </svg>
      );
    case 'audio':
      return (
        <svg {...common}>
          <path d="M9 18V5l12-2v13" />
          <circle cx="6" cy="18" r="3" />
          <circle cx="18" cy="16" r="3" />
        </svg>
      );
    case 'video':
      return (
        <svg {...common}>
          <rect x="2" y="6" width="14" height="12" rx="2" />
          <path d="m22 8-6 4 6 4z" />
        </svg>
      );
    case 'lock':
      return (
        <svg {...common}>
          <rect x="4" y="11" width="16" height="10" rx="2" />
          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        </svg>
      );
    case 'bolt':
      return (
        <svg {...common}>
          <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
        </svg>
      );
    case 'wifi-off':
      return (
        <svg {...common}>
          <path d="M2 2l20 20" />
          <path d="M8.5 16.5a5 5 0 0 1 7 0" />
          <path d="M2 8.82a15 15 0 0 1 4.17-2.65" />
          <path d="M10.66 5c4.01-.36 8.14.9 11.34 3.76" />
          <path d="M16.85 11.25a10 10 0 0 1 2.22 1.68" />
          <path d="M5 13a10 10 0 0 1 5.17-2.69" />
          <line x1="12" y1="20" x2="12.01" y2="20" />
        </svg>
      );
    case 'history':
      return (
        <svg {...common}>
          <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
          <path d="M3 3v5h5" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case 'check':
      return (
        <svg {...common} width={18} height={18}>
          <path d="M20 6 9 17l-5-5" />
        </svg>
      );
    default:
      return null;
  }
};

function App() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="App">
      <header className={`nav ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="container nav-inner">
          <a href="#top" className="nav-brand">
            <img src={appIcon} alt="MediaConvert Pro" className="nav-logo" />
            <span>MediaConvert Pro</span>
          </a>
          <nav className="nav-links">
            <a href="#features">Features</a>
            <a href="#privacy">Privacy</a>
            <a href="#pricing">Pricing</a>
            <a href="#download" className="nav-cta">Download</a>
          </nav>
        </div>
      </header>

      <main id="top">
        {/* HERO */}
        <section className="hero">
          <div className="hero-glow" aria-hidden="true" />
          <div className="container hero-inner">
            <div className="hero-copy">
              <div className="eyebrow">
                <span className="eyebrow-dot" />
                Built for iPhone · 100% on-device
              </div>
              <h1>
                Your iPhone is now a<br />
                <span className="gradient-text">file conversion studio.</span>
              </h1>
              <p className="lede">
                Convert images, video, audio, and PDFs in seconds. No desktop. No uploads.
                No waiting. Your files never leave your device.
              </p>
              <div className="hero-ctas">
                <a href="#download" className="btn btn-primary">
                  Download on the App Store
                </a>
                <a href="#features" className="btn btn-ghost">
                  See what it does →
                </a>
              </div>
              <div className="hero-meta">
                <div><strong>5</strong> free conversions</div>
                <div className="meta-divider" />
                <div>No account required</div>
                <div className="meta-divider" />
                <div>No credit card</div>
              </div>
            </div>

            <div className="hero-visual">
              <div className="phone">
                <div className="phone-screen">
                  <img src={appIcon} alt="" className="phone-icon" />
                  <div className="phone-title">MediaConvert Pro</div>
                  <div className="phone-subtitle">Convert your media with ease</div>
                  <div className="phone-tiles">
                    <div className="tile"><span className="tile-icon icon-orange"><Icon name="pdf" /></span><span>Image to PDF</span></div>
                    <div className="tile"><span className="tile-icon icon-blue"><Icon name="image" /></span><span>Convert Image</span></div>
                    <div className="tile"><span className="tile-icon icon-purple"><Icon name="audio" /></span><span>Extract Audio</span></div>
                    <div className="tile"><span className="tile-icon icon-amber"><Icon name="video" /></span><span>Convert Video</span></div>
                  </div>
                </div>
                <img src={iphoneBezel} alt="" className="phone-bezel" />
              </div>
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section id="features" className="section">
          <div className="container">
            <div className="section-head">
              <div className="kicker">Everything you need</div>
              <h2>Four conversions. One tap each.</h2>
              <p className="sub">
                Powered by Apple's own frameworks — Core Image, PDFKit, and AVFoundation —
                so everything is fast, native, and private.
              </p>
            </div>

            <div className="grid features">
              {features.map((f) => (
                <div key={f.title} className="card">
                  <div className={`card-icon icon-${f.color}`}><Icon name={f.icon} /></div>
                  <h3>{f.title}</h3>
                  <div className="card-formats">{f.formats}</div>
                  <p>{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PRIVACY */}
        <section id="privacy" className="section section-privacy">
          <div className="container privacy-inner">
            <div className="privacy-copy">
              <div className="kicker">Built for privacy</div>
              <h2>Your files never leave your device.</h2>
              <p>
                Unlike web-based converters that upload your files to a server,
                MediaConvert Pro processes everything locally using Apple's Core Image,
                PDFKit, and AVFoundation frameworks. No cloud. No tracking. No risk.
              </p>
              <ul className="privacy-list">
                <li><Icon name="lock" /> 100% on-device processing</li>
                <li><Icon name="wifi-off" /> Works offline — airplane mode friendly</li>
                <li><Icon name="bolt" /> Native Apple frameworks for blazing speed</li>
                <li><Icon name="history" /> Conversion history saved locally, only on your phone</li>
              </ul>
            </div>
            <div className="privacy-visual">
              <div className="privacy-card">
                <Icon name="lock" />
                <div className="privacy-stat">0</div>
                <div className="privacy-label">Files uploaded. Ever.</div>
              </div>
            </div>
          </div>
        </section>

        {/* HISTORY */}
        <section className="section section-tight">
          <div className="container two-col">
            <div>
              <div className="kicker">Conversion history</div>
              <h2>Convert once. Re-share forever.</h2>
              <p className="sub">
                Every converted file is automatically saved to your history. Tap any record
                to share instantly — no need to convert again.
              </p>
            </div>
            <div className="history-mock">
              <div className="history-row"><span className="tile-icon icon-orange"><Icon name="pdf" /></span><div><div className="hr-title">Receipts_May.pdf</div><div className="hr-sub">From 4 images · Today</div></div></div>
              <div className="history-row"><span className="tile-icon icon-blue"><Icon name="image" /></span><div><div className="hr-title">vacation.jpeg</div><div className="hr-sub">From HEIC · Yesterday</div></div></div>
              <div className="history-row"><span className="tile-icon icon-purple"><Icon name="audio" /></span><div><div className="hr-title">interview.m4a</div><div className="hr-sub">From MP4 · 2 days ago</div></div></div>
              <div className="history-row"><span className="tile-icon icon-amber"><Icon name="video" /></span><div><div className="hr-title">family_clip.mp4</div><div className="hr-sub">From MOV · Last week</div></div></div>
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section id="pricing" className="section">
          <div className="container">
            <div className="section-head">
              <div className="kicker">Pricing</div>
              <h2>Free to try. Pro when you're ready.</h2>
              <p className="sub">
                Start with 5 free conversions — no account, no credit card. Upgrade any time.
              </p>
            </div>

            <div className="grid pricing">
              {plans.map((p) => (
                <div key={p.name} className={`price-card ${p.highlight ? 'price-highlight' : ''}`}>
                  {p.badge && <div className="badge">{p.badge}</div>}
                  <div className="price-name">{p.name}</div>
                  <div className="price-amount">
                    <span className="price-value">{p.price}</span>
                    <span className="price-period">{p.period}</span>
                  </div>
                  <div className="price-tagline">{p.tagline}</div>
                  <ul className="price-features">
                    {p.features.map((feat) => (
                      <li key={feat}><Icon name="check" /> {feat}</li>
                    ))}
                  </ul>
                  <a href="#download" className={`btn ${p.highlight ? 'btn-primary' : 'btn-outline'} btn-block`}>
                    {p.cta}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section id="download" className="section section-cta">
          <div className="container cta-inner">
            <img src={appIcon} alt="" className="cta-icon" />
            <h2>Ready to convert?</h2>
            <p>Download MediaConvert Pro and start converting in seconds.</p>
            <a href="https://apps.apple.com/" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-large">
              Download on the App Store
            </a>
            <div className="cta-meta">Requires iOS · Free with in-app purchases</div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container footer-inner">
          <div className="footer-brand">
            <img src={appIcon} alt="" className="footer-logo" />
            <span>MediaConvert Pro</span>
          </div>
          <div className="footer-links">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#download">Download</a>
            <a href="/support">Support</a>
            <a href="/legal">Legal</a>
          </div>
          <div className="footer-copy">
            © {new Date().getFullYear()} MediaConvert Pro. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
